<script lang="ts">
  import { onMount } from "svelte"
  import Layout from "$app/Layouts/Alignment.svelte"
  import { Pulse } from "svelte-loading-spinners"
  import { fade } from "svelte/transition"
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import noUiSlider from "nouislider"
  import "nouislider/dist/nouislider.css"
  import { appStore } from "$app/store.svelte"
  import {
    ChevronsRight,
    ChevronsLeft,
    CircleCheck,
    Circle,
    AlertTriangle
  } from "lucide-svelte"

  interface Props {
    plan: PlanType
    alignment_test: any
    alignment_test_response: any
  }
  let props = $props()

  let { plan, alignment_test }: Props = props
  let alignment_test_response = $state(props.alignment_test_response)

  let test_image = new URL(`/images/alignment_test/test.jpg`, import.meta.url)
    .href
  let values_image = new URL(
    `/images/alignment_test/value-alignment.jpg`,
    import.meta.url
  ).href

  let sliderEl: HTMLElement
  let response: HTMLInputElement = $state(null)
  let responseValue = $state(null)
  let responseValues = $state([])

  let loading: boolean = $state(true)
  let timeout: boolean = $state(false)

  function initializeSlider() {
    sliderEl = document.getElementById("slider")
    if (
      (appStore.alignment_current_step === 1 ||
        appStore.alignment_current_step === 6) &&
      sliderEl
    ) {
      noUiSlider.create(sliderEl, {
        start: [1],
        step: 1,
        connect: "lower",
        range: {
          min: [1],
          max: [10]
        },
        pips: {
          mode: "steps",
          stepped: true,
          density: 10
        }
      })
      sliderEl.noUiSlider.set(responseValue)
      sliderEl.noUiSlider.on("update", function (values, handle) {
        var value = values[handle]
        responseValue = parseInt(value, 10)
      })
    }
  }

  function updateResponse() {
    responseValue = ""
    responseValues = []

    if ([1, 2, 3, 6].includes(appStore.alignment_current_step)) {
      if (
        !alignment_test_response.responses[
          `q${appStore.alignment_current_step}`
        ]
      ) {
        loading = false
        return
      }
      responseValue =
        alignment_test_response.responses[`q${appStore.alignment_current_step}`]
    } else if (appStore.alignment_current_step == 4) {
      ;[0, 1, 2, 3, 4].forEach((count) => {
        if (alignment_test_response.responses[`value${count + 1}`]) {
          responseValues[count] = JSON.parse(
            alignment_test_response.responses[`value${count + 1}`]
          )

          if (responseValues[count] == "undefined") {
            responseValues[count] = ""
          }
        }
      })
    } else if (appStore.alignment_current_step == 5) {
      ;[0, 1, 2].forEach((count) => {
        if (alignment_test_response.responses[`goal${count + 1}`]) {
          responseValues[count] = JSON.parse(
            alignment_test_response.responses[`goal${count + 1}`]
          )
        }
        if (responseValues[count] == "undefined") {
          responseValues[count] = ""
        }
      })
    }

    loading = false
  }

  let errorMsg = $state("")
  async function nextStep() {
    errorMsg = ""

    const postFormData = new FormData()
    postFormData.append(
      "alignment_test_response[current_step]",
      appStore.alignment_current_step
    )
    if ([1, 2, 3, 6].includes(appStore.alignment_current_step)) {
      postFormData.append(
        `alignment_test_response[responses][q${appStore.alignment_current_step}]`,
        responseValue
      )
    } else if (appStore.alignment_current_step == 4) {
      ;[0, 1, 2, 3, 4].forEach((count) => {
        postFormData.append(
          `alignment_test_response[responses][value${count + 1}][]`,
          responseValues[count]
        )
      })
    } else if (appStore.alignment_current_step == 5) {
      ;[0, 1, 2].forEach((count) => {
        postFormData.append(
          `alignment_test_response[responses][goal${count + 1}][]`,
          responseValues[count]
        )
      })
    }

    //console.log(postFormData)
    try {
      const resp = await xhr.patch(
        `/alignment_test/${alignment_test_response.access_token}`,
        postFormData
      )

      console.log(resp.data)
      alignment_test_response = resp.data
      appStore.alignment_current_step = alignment_test_response.current_step
      updateResponse()
    } catch (e) {
      toast("There was an error submiting your response", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    }
  }

  // This function is triggered if the user runs out of time.
  // Send blanks for any questions not yet answered.
  async function submitTimeout() {
    timeout = false

    const postFormData = new FormData()
    postFormData.append("alignment_test_response[current_step]", "6")
    postFormData.append("alignment_test_response[timeout]", "true")
    if (appStore.alignment_current_step == 6) {
      postFormData.append(
        `alignment_test_response[responses][q6]`,
        responseValue
      )
    } else if (appStore.alignment_current_step < 6) {
      postFormData.append(`alignment_test_response[responses][q6]`, undefined)
    }

    if (appStore.alignment_current_step == 5) {
      ;[0, 1, 2].forEach((count) => {
        postFormData.append(
          `alignment_test_response[responses][goal${count + 1}][]`,
          responseValues[count]
        )
      })
    } else if (appStore.alignment_current_step < 5) {
      ;[0, 1, 2].forEach((count) => {
        postFormData.append(
          `alignment_test_response[responses][goal${count + 1}][]`,
          ""
        )
      })
    }

    if (appStore.alignment_current_step == 4) {
      ;[0, 1, 2, 3, 4].forEach((count) => {
        postFormData.append(
          `alignment_test_response[responses][value${count + 1}][]`,
          responseValues[count]
        )
      })
    } else if (appStore.alignment_current_step < 4) {
      ;[0, 1, 2, 3, 4].forEach((count) => {
        postFormData.append(
          `alignment_test_response[responses][value${count + 1}][]`,
          ""
        )
      })
    }

    if (appStore.alignment_current_step == 3) {
      postFormData.append(
        `alignment_test_response[responses][q3]`,
        responseValue
      )
    } else if (appStore.alignment_current_step < 3) {
      postFormData.append(`alignment_test_response[responses][q3]`, "")
    }

    if (appStore.alignment_current_step == 2) {
      postFormData.append(
        `alignment_test_response[responses][q2]`,
        responseValue
      )
    } else if (appStore.alignment_current_step < 2) {
      postFormData.append(`alignment_test_response[responses][q2]`, "")
    }

    if (appStore.alignment_current_step == 1) {
      postFormData.append(
        `alignment_test_response[responses][q2]`,
        responseValue
      )
    }

    //console.log(postFormData)
    try {
      const resp = await xhr.patch(
        `/alignment_test/${alignment_test_response.access_token}`,
        postFormData
      )

      alignment_test_response = resp.data
      appStore.alignment_current_step = alignment_test_response.current_step
      updateResponse()
    } catch (e) {
      toast("There was an error submiting your response", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    }
  }

  async function startTest() {
    const postFormData = new FormData()
    postFormData.append("alignment_test_response[current_step]", "0")
    try {
      const resp = await xhr.patch(
        `/alignment_test/${alignment_test_response.access_token}`,
        postFormData
      )

      alignment_test_response = resp.data
      appStore.alignment_current_step = alignment_test_response.current_step
      updateResponse()
    } catch (e) {
      toast("There was an error starting the alignment test.", "error")
    }
  }

  async function prevStep() {
    if (appStore.alignment_current_step > 1) {
      appStore.alignment_current_step = appStore.alignment_current_step - 1
      alignment_test_response.current_step = appStore.alignment_current_step
    }
    updateResponse()
  }

  function pad(n) {
    return (n < 10 ? "0" : "") + n
  }

  let countdown = $state("")
  let countdownInterval

  function updateTimer() {
    let future = Date.parse(alignment_test_response.expired_at)
    let now = new Date().getTime()
    let diff = future - now

    let minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    let seconds = Math.floor((diff % (1000 * 60)) / 1000)

    if (minutes <= 0 && seconds <= 0) {
      countdown = "Time Up"
      submitTimeout()
      clearInterval(countdownInterval)
    } else {
      countdown = minutes + ":" + pad(seconds) + " remaining"
    }
  }

  onMount(() => {
    timeout = alignment_test_response.timeout
    appStore.alignment_current_step = alignment_test_response.current_step

    updateResponse()
    if (alignment_test.has_time_limit) {
      updateTimer()
      countdownInterval = setInterval(updateTimer, 500)
    }
  })
</script>

<Layout {...props}>
  <div
    class="bg-gradient-to-r from-blue-600 to-indigo-800 z-20 lg:mt-8"
    id="header"
  >
    <div class="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
      <div
        class="grid grid-cols-1 md:grid-cols-2 justify-center md:justify-between gap-y-3 py-4"
      >
        <div
          class="flex items-center justify-center md:justify-start row-start-2 md:row-start-1"
        >
          {#if alignment_test_response.status === "complete"}
            <p
              class="ml-2 lg:ml-0 text leading-5 font-medium text-green-200 flex items-center mr-2"
            >
              Completed
            </p>
            <Circle class="h-8 w-8 text-green-200" />
          {:else if alignment_test_response.status == "invited"}
            <img
              src={plan.logo_url}
              class="w-6 h-6 md:w-10 md:h-10 bg-white rounded-lg p-0.5 shadow"
              alt="LWP Logo"
            />
            <p class="ml-2 text-white text-base md:text-xl">{plan.name}</p>
          {:else}
            <nav
              class="flex items-center justify-start flex-col pl-4 lg:pl-0 lg:flex-wrap"
            >
              <p
                class="text-sm lg:text-base leading-5 font-medium text-white flex items-center justify-start"
              >
                Question {appStore.alignment_current_step} of 6
              </p>
              <ul
                class="mt-2 lg:ml-6 flex items-center justify-between space-x-3 md:space-x-4"
              >
                {#each [1, 2, 3, 4, 5, 6] as index}
                  {#if appStore.alignment_current_step > index}
                    <li>
                      <!-- Completed Step -->
                      <a
                        href="#"
                        aria-label="Completed Step"
                        class="block w-2.5 h-2.5 bg-green-500 rounded-full hover:bg-blue-100 focus:bg-blue-200"
                      ></a>
                    </li>
                  {:else if appStore.alignment_current_step === index}
                    <li>
                      <!-- Current Step -->
                      <div class="relative flex items-center justify-center">
                        <span class="absolute w-5 h-5 p-px flex">
                          <span class="w-full h-full rounded-full bg-indigo-200"
                          ></span>
                        </span>
                        <span
                          class="relative block w-2.5 h-2.5 bg-blue-600 rounded-full"
                        ></span>
                      </div>
                    </li>
                  {:else}
                    <li>
                      <a
                        href="#"
                        aria-label="Step"
                        class="block w-2.5 h-2.5 bg-gray-200 rounded-full hover:bg-blue-400 focus:bg-blue-400"
                      ></a>
                    </li>
                  {/if}
                {/each}
              </ul>
            </nav>
          {/if}
        </div>
        <div
          class="ml-6 text-center md:text-right flex items-center justify-center md:justify-end row-start-1 md:row-start-1"
        >
          <div>
            <div class="text-blue-100 font-bold text-lg">
              {#if alignment_test_response.test_type === "pre"}
                Pre-Alignment Test
              {:else if alignment_test_response.test_type == "post"}
                Post-Alignment Test
              {/if}
            </div>
            <div class="text-gray-100 font-bold text-sm md:text-base">
              for {alignment_test_response.user.email}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  {#if alignment_test_response.status === "complete"}
    <section
      class="py-6 md:py-12 px-4 md:px-8 bg-white shadow"
      id="step_content"
      transition:fade
    >
      {#if timeout}
        <div
          class="mx-auto max-w-4xl flex items-center p-4 border border-orange-100 bg-orange-50 rounded shadow"
        >
          <div class="py-6 px-2 lg:px-6">
            <AlertTriangle
              class="h-16 lg:h-24 w-16 lg:w-24 text-orange-500 mx-auto"
            />
          </div>

          <div class="py-4 lg:py-6">
            <p
              class="text-lg md:text-3xl font-bold px-3 md:px-6 text-orange-800"
            >
              NOTE: You have run out of time to complete all the answers.
            </p>
            <p
              class="text-base md:text-2xl font-bold mt-4 md:mt-6 px-3 md:px-6"
            >
              Your completed answers were successfully submitted and the rest
              have been marked as incomplete.
            </p>
          </div>
        </div>
      {:else}
        <div
          class="mx-auto max-w-4xl flex items-center p-4 border border-green-100 bg-green-50 rounded shadow"
        >
          <div class="py-6 px-6">
            <CircleCheck class="h-24 w-24 text-green-500 mx-auto" />
          </div>

          <div class="py-6">
            <p class="text-lg md:text-3xl font-bold px-3 md:px-6">
              Thank you for completing this alignment test.
            </p>
            <p class="text-lg md:text-3xl font-bold mt-4 md:mt-6 px-3 md:px-6">
              Your answers were successfully submitted.
            </p>
          </div>
        </div>
      {/if}

      <div class="py-6">
        <img
          src={values_image}
          class="w-full h-auto mx-auto max-w-4xl rounded-lg shadow"
          alt="Values"
        />
      </div>
    </section>
  {:else if alignment_test_response.status == "invited"}
    <div
      class="relative isolate overflow-hidden bg-gradient-to-b from-indigo-100/20 md:pt-8"
    >
      <div
        class="absolute inset-y-0 right-1/2 -z-10 -mr-96 w-[200%] origin-top-right skew-x-[-30deg] bg-white shadow-xl shadow-indigo-600/10 ring-1 ring-indigo-50 sm:-mr-80 lg:-mr-96"
        aria-hidden="true"
      ></div>
      <div class="mx-auto max-w-7xl px-6 py-12 sm:py-16 lg:px-8">
        <div
          class="mx-auto max-w-2xl lg:mx-0 lg:grid lg:max-w-none lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 xl:grid-cols-1 xl:grid-rows-1 xl:gap-x-8"
        >
          <h1
            class="max-w-2xl text-2xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:col-span-2 xl:col-auto"
          >
            {#if alignment_test_response.test_type === "pre"}
              {alignment_test_response.user.name}, welcome to your Pre-Alignment
              Test!
            {:else if alignment_test_response.test_type === "post"}
              {alignment_test_response.user.name}, welcome to your
              Post-Alignment Test!
            {/if}
          </h1>
          <div class="mt-6 max-w-xl lg:mt-0 xl:col-end-1 xl:row-start-1">
            <p class="text-base lg:text-lg leading-8 text-gray-600">
              {#if alignment_test_response.test_type === "pre"}
                You have been invited to take part in an Alignment Test for {plan.name}.
                As you proceed, please answer each of the following questions to
                your best ability without talking to anyone in your company or
                looking at any materials.
              {:else if alignment_test_response.test_type === "post"}
                Welcome back to your Post-Alignment Test for {plan.name}. As you
                proceed, please answer each of the following questions to your
                best ability without talking to anyone in your company or
                looking at any materials.
              {/if}
            </p>
            {#if alignment_test.has_time_limit}
              <p
                class="mt-4 text-base md:text-lg text-amber-800 p-4 lg:p-5 border-amber-100 bg-amber-50 rounded shadow"
              >
                <span class="font-bold">PLEASE NOTE:</span> You have been
                allocated
                <span class="font-bold text-xl text-amber-900"
                  >{alignment_test.time_limit} minutes</span
                > to complete the test. The timer will begin as soon as you click
                on the "Get started" button below.
              </p>
            {/if}
            <p class="text-base lg:text-lg leading-8 text-gray-600 mt-6">
              {#if alignment_test.has_time_limit}
                When you are ready to begin click on the "Get Started" button
                and your {alignment_test.time_limit}
                minute countdown will begin.
              {:else}
                When you are ready to begin click on the "Get Started" button.
              {/if}
            </p>
            <div class="mt-6 lg:mt-10 flex items-center gap-x-6">
              <button
                onclick={startTest}
                class="rounded-md bg-amber-600 px-3.5 py-2.5 text-lg font-semibold text-white shadow-sm hover:bg-indigo-500"
                >Get started</button
              >
            </div>
          </div>
          <img
            src={test_image}
            alt=""
            class="mt-10 aspect-[6/5] w-full max-w-lg rounded-2xl object-cover sm:mt-16 lg:mt-0 lg:max-w-none xl:row-span-2 xl:row-end-2 xl:mt-36"
          />
        </div>
      </div>
      <div
        class="absolute inset-x-0 bottom-0 -z-10 h-24 bg-gradient-to-t from-white sm:h-32"
      ></div>
    </div>
  {:else if loading}
    <section class="py-0 md:py-4 px-8 bg-white shadow mb-12" transition:fade>
      <div class="py-12 flex justify-center w-full">
        <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
      </div>
    </section>
  {:else}
    <section
      class="py-12 px-8 bg-white shadow relative"
      id="step_content"
      transition:fade
    >
      {#if alignment_test.has_time_limit && countdown.length > 0}
        <div
          class="absolute top-2 right-4 lg:right-5 border border-reed-800 bg-red-100 text-red-800 py-2 lg:py-3 px-4 rounded-md shadow text-base sm:text-lg md:text-xl"
        >
          {countdown}
        </div>
      {/if}

      {#if appStore.alignment_current_step === 1}
        <input type="hidden" bind:this={response} bind:value={responseValue} />
        <p class="text-lg md:text-3xl font-bold mt-6 md:mt-10 px-2 md:px-6">
          1. On a scale of 1-10, how aligned and on the same page is your team?
        </p>
        <h6 class="mt-4 text-xs md:text-md text-center">
          MOVE THE SLIDER BELOW TO SELECT YOUR RESPONSE
        </h6>

        <div class="max-w-4xl mt-12 pb-6 mx-auto px-4 lg:px-8">
          <div class="w-full" id="slider" use:initializeSlider></div>
        </div>
        <div class="max-w-4xl mt-8 pb-12 md:mb-20 mx-auto grid grid-cols-3">
          <div class="text-xs md:text-sm text-gray-500 font-bold text-left">
            Not at all
          </div>
          <div class="text-xs md:text-sm text-gray-500 font-bold text-center">
            Somewhat
          </div>
          <div class="text-xs md:text-sm text-gray-500 font-bold text-right">
            Fully Aligned
          </div>
        </div>
      {:else if appStore.alignment_current_step === 2}
        <p class="text-lg md:text-3xl font-bold mt-6 md:mt-10 px-3 md:px-6">
          {#if alignment_test_response.test_type === "pre"}
            2. What is the company's Mission?
          {:else if alignment_test_response.test_type == "post"}
            2. What is the company's Brand Purpose?
          {/if}
        </p>
        <div class="mt-6 pb-12 md:mb-8 mx-3 md:mx-6">
          <textarea
            bind:value={responseValue}
            class="form-textarea block max-w-3xl mt-1 w-full"
            rows="3"
            placeholder="Please enter your response."
          ></textarea>
        </div>
      {:else if appStore.alignment_current_step === 3}
        <p class="text-lg md:text-3xl font-bold mt-6 md:mt-10 px-3 md:px-6">
          {#if alignment_test_response.test_type === "pre"}
            3. What is the company's Vision?
          {:else if alignment_test_response.test_type == "post"}
            3. What is the company's Blue Sky Vision?
          {/if}
        </p>
        <div class="mt-6 pb-12 md:mb-8 mx-3 md:mx-6">
          <textarea
            bind:value={responseValue}
            class="form-textarea block max-w-3xl mt-1 w-full"
            rows="3"
            placeholder="Please enter your response."
          ></textarea>
        </div>
      {:else if appStore.alignment_current_step === 4}
        <p class="text-lg md:text-3xl font-bold mt-6 md:mt-10 px-3 md:px-6">
          {#if alignment_test_response.test_type === "pre"}
            4. What are the top five values of the company?
          {:else if alignment_test_response.test_type == "post"}
            4. What are the top five Values of the company from most to least
            important?
          {/if}
        </p>
        <div
          class="mt-4 md:mt-10 space-y-3 md:space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-2 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:pb-0"
        >
          {#each [0, 1, 2, 3, 4] as index}
            <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
              <label
                for="first-name"
                class="block text-base font-medium leading-6 text-gray-900 sm:pt-1.5 text-left md:text-right"
                >Value {index + 1}</label
              >
              <div class="mt-2 sm:col-span-2 sm:mt-0">
                <input
                  type="text"
                  bind:value={responseValues[index]}
                  class="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          {/each}
        </div>
      {:else if appStore.alignment_current_step === 5}
        <p class="text-lg md:text-3xl font-bold mt-6 md:mt-10 px-3 md:px-6">
          {#if alignment_test_response.test_type === "pre"}
            5. What are your team's top 3 annual goals?
          {:else if alignment_test_response.test_type == "post"}
            5. What are your team's top 3 Annual Goals this year?
          {/if}
        </p>
        <div
          class="mt-4 md:mt-10 space-y-3 md:space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-2 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:pb-0"
        >
          {#each [0, 1, 2] as index}
            <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
              <label
                for="first-name"
                class="block text-base font-medium leading-6 text-gray-900 sm:pt-1.5 text-left md:text-right"
                >Goal #{index + 1}</label
              >
              <div class="mt-2 sm:col-span-2 sm:mt-0">
                <input
                  type="text"
                  bind:value={responseValues[index]}
                  class="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          {/each}
        </div>
      {:else if appStore.alignment_current_step === 6}
        <input type="hidden" bind:this={response} bind:value={responseValue} />
        <p class="text-lg md:text-3xl font-bold mt-6 md:mt-10 px-3 md:px-6">
          6. Rate how important it is for people to know the answers to these
          questions.
        </p>
        <h6 class="mt-4 text-xs md:text-md text-center">
          MOVE THE SLIDER BELOW TO SELECT YOUR RESPONSE
        </h6>

        <div class="max-w-4xl mt-12 pb-6 mx-auto px-8">
          <div class="w-full" id="slider" use:initializeSlider></div>
        </div>
        <div class="max-w-4xl mt-8 pb-12 md:mb-20 mx-auto grid grid-cols-3">
          <div class="text-xs md:text-sm text-gray-500 font-bold text-left">
            Not Important
          </div>
          <div class="text-xs md:text-sm text-gray-500 font-bold text-center">
            Somewhat
          </div>
          <div class="text-xs md:text-sm text-gray-500 font-bold text-right">
            Very Important
          </div>
        </div>
      {/if}

      {#if errorMsg.length > 0}
        <div
          transition:fade
          class="mt-6 flex items-center justify-center max-w-2xl mx-auto bg-red-200 text-lg text-red-700 px-8 py-4 rounded"
        >
          {errorMsg}
        </div>
      {/if}

      <div class="flex items-center justify-center gap-x-8 py-4 md:py-8">
        {#if appStore.alignment_current_step > 1 && appStore.alignment_current_step <= 6}
          <button
            onclick={prevStep}
            class="rounded-lg bg-slate-400 shadow-sm text-white flex items-center justify-center text-base md:text-lg py-2 md:py-4 px-10 hover:shadow-lg hover:bg-slate-500 cursor-pointer"
          >
            <ChevronsLeft class="h-10 w-10 mr-2" />
            BACK</button
          >
        {/if}
        {#if appStore.alignment_current_step >= 1}
          <button
            onclick={nextStep}
            class="rounded-lg bg-blue-600 shadow-sm text-white flex items-center justify-center text-base md:text-lg py-2 md:py-4 px-10 hover:shadow-lg hover:bg-blue-700 cursor-pointer"
          >
            {#if appStore.alignment_current_step == 6}
              COMPLETE
              <CircleCheck class="h-10 w-10 ml-2" />
            {:else}
              NEXT
              <ChevronsRight class="h-10 w-10 ml-2" />
            {/if}
          </button>
        {/if}
      </div>
    </section>
  {/if}
</Layout>
